'use client';

import { Button, ButtonGroup, Grid, Heading, LoadingIndicator } from '@petsmart-ui/sparky';
import { useFormikContext } from 'formik';
import { type MouseEvent, useEffect, useMemo, useState } from 'react';

import { useAlertBanner, useUserSession } from '@libs/common-next';

import { BillingAddressSelector } from '../BillingAddressSelector';
import { DEFAULT_ADDRESS_FORM_STATE, MAX_ADDRESSES } from '../PaymentForm/constants';
import type { PaymentFormInterface } from '../PaymentForm/types';
import { BillingAddressForm } from './BillingAddressForm';
import type { BillingAddressProps } from './types';
import './BillingAddress.scss';

export function BillingAddress<T extends PaymentFormInterface = PaymentFormInterface>({
    onCancel,
    handleErrorBanner,
    isLoading,
    disableUntilDirty,
    addAddressIconSize = 'sm',
    defaultAddressId,
    hideCancelCta = false,
    submitCtaText = 'Save',
}: Readonly<BillingAddressProps<T>>) {
    const [showForm, setShowForm] = useState<boolean>(false);

    const { userData } = useUserSession();
    const { addresses } = { ...userData };

    const { setAlert } = useAlertBanner();

    const { setValues, values, submitForm, touched, setTouched, validateForm, dirty } = useFormikContext<T>();

    const addressListLoading = addresses === undefined;

    const sortedAddresses = useMemo(() => {
        if (!addresses || !defaultAddressId) return addresses;

        const selectedAddress = addresses.find(addr => addr.addressId.toString() === defaultAddressId);
        const otherAddresses = addresses.filter(addr => addr.addressId.toString() !== defaultAddressId);

        return selectedAddress ? [selectedAddress, ...otherAddresses] : addresses;
    }, [addresses, defaultAddressId]);

    useEffect(() => {
        if (addresses === null || addresses?.length === 0) {
            setShowForm(true);
        }
    }, [addresses]);

    const scrollToBillingSection = () => {
        const title = document.querySelector<HTMLElement>('[data-testid="billing-address"]');
        if (title) {
            title.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const handleAddNewAddressClick = () => {
        setValues({
            ...values,
            address: DEFAULT_ADDRESS_FORM_STATE,
            firstName: '',
            lastName: '',
            phone: '',
            addressType: 'form',
        });
        setTouched({
            ...touched,
            address: false,
            firstName: false,
            lastName: false,
            phone: false,
        });
        setShowForm(true);
        requestAnimationFrame(() => {
            scrollToBillingSection();
        });
    };

    const handleCancelClick = () => {
        setAlert(null);

        if (addresses?.length && showForm) {
            setValues({
                ...values,
                addressType: 'id',
                address: undefined,
                firstName: undefined,
                lastName: undefined,
                phone: undefined,
            });
            setShowForm(false);
        } else {
            onCancel();
        }
    };

    const handleSubmitClick = async (e: MouseEvent<Element>) => {
        e.preventDefault();

        const validationErrors = await validateForm();

        if (Object.keys(validationErrors).length > 0) {
            const touchedFields = Object.keys(validationErrors).reduce(
                (acc, key) => {
                    const errorValue = validationErrors[key as keyof typeof validationErrors];
                    if (typeof errorValue === 'object' && errorValue !== null) {
                        acc[key] = Object.keys(errorValue).reduce(
                            (nestedAcc, nestedKey) => {
                                nestedAcc[nestedKey] = true;
                                return nestedAcc;
                            },
                            {} as Record<string, boolean>
                        );
                    } else {
                        acc[key] = true;
                    }
                    return acc;
                },
                {} as Record<string, boolean | Record<string, boolean>>
            );

            setTouched({ ...touched, ...touchedFields }, false);
            handleErrorBanner(validationErrors);
        } else {
            const isValid = handleErrorBanner();
            if (isValid) {
                submitForm();
            }
        }
    };

    return (
        <div className="billing-address" data-testid="billing-address">
            <Heading tagName="h6" size="body-lg-bold" data-testid="billing-address-title">
                Billing address
            </Heading>

            <Grid>
                {addressListLoading && (
                    <Grid.Item>
                        <LoadingIndicator iconSize="4xl" data-testid="loading-indicator" />
                    </Grid.Item>
                )}

                {!!sortedAddresses?.length && sortedAddresses?.length !== 0 && !showForm && (
                    <Grid.Item className="address-selector-item">
                        <BillingAddressSelector billingAddresses={sortedAddresses} />
                        <Button
                            data-testid="add-address-btn"
                            className="billing-address__add-address-btn"
                            type="button"
                            text="Add new address"
                            variant="link"
                            iconName="plus"
                            iconSize={addAddressIconSize}
                            iconPosition="before"
                            disabled={(addresses?.length ?? 0) >= MAX_ADDRESSES}
                            onClick={handleAddNewAddressClick}
                        />
                    </Grid.Item>
                )}

                {showForm && (
                    <Grid.Item>
                        <BillingAddressForm />
                    </Grid.Item>
                )}

                <Grid.Item>
                    <ButtonGroup
                        behavior="responsive"
                        className="billing-address__button-group"
                        data-testid="billing-address-button-group"
                    >
                        {!hideCancelCta && (
                            <Button
                                disabled={isLoading}
                                text="Cancel"
                                size="lg"
                                type="button"
                                fullWidth
                                onClick={handleCancelClick}
                            />
                        )}
                        <Button
                            loading={isLoading}
                            disabled={isLoading || (disableUntilDirty && !dirty)}
                            text={submitCtaText}
                            size="lg"
                            type="submit"
                            variant="primary"
                            className="billing-address__save-cta"
                            fullWidth
                            onClick={e => {
                                handleSubmitClick(e);
                            }}
                        />
                    </ButtonGroup>
                </Grid.Item>
            </Grid>
        </div>
    );
}
